// ============================================================================
// Beacon — the "buy box": the filters that define the deals you actually want.
// Price band, property type (duplex → 5+), market, min score, flood, min cap.
// Applied live to the deal-finder shortlist, and saved so the same criteria can
// later drive a daily auto-search. Same dual-mode repo pattern as scenarios.
// ============================================================================

import type { Deal, Metrics } from "../types";
import { getSupabase, isCloud } from "./supabase";

export type PropType = "any" | "duplex" | "triplex" | "fourplex" | "multi5";

export interface BuyBox {
  id: string;
  name: string;
  priceMin: number | null;
  priceMax: number | null;
  propertyType: PropType;
  markets: string[]; // match if deal.market contains any (case-insensitive)
  minScore: number | null;
  floodXOnly: boolean; // only zone X (no special flood hazard)
  minCap: number | null; // cap-on-cost floor (fraction, e.g. 0.065)
  minCeiling: number | null; // 2BR SAFMR floor ($)
  excludeGated: boolean; // drop deals stopped by a failed gate
}

export function defaultBuyBox(): BuyBox {
  return {
    id: newBuyBoxId(),
    name: "My buy box",
    priceMin: null,
    priceMax: null,
    propertyType: "any",
    markets: [],
    minScore: null,
    floodXOnly: false,
    minCap: null,
    minCeiling: null,
    excludeGated: true,
  };
}

const UNITS: Record<PropType, [number, number]> = {
  any: [0, Infinity],
  duplex: [2, 2],
  triplex: [3, 3],
  fourplex: [4, 4],
  multi5: [5, Infinity],
};

/** A single reason a deal fell outside the buy box (for honest UI). */
export function buyBoxMisses(deal: Deal, m: Metrics, box: BuyBox): string[] {
  const misses: string[] = [];
  if (box.excludeGated && m.score.gated) misses.push("stopped by a gate");
  if (box.minScore != null && (m.score.gated || m.score.composite < box.minScore)) misses.push(`score < ${box.minScore}`);
  const price = deal.purchase;
  if (box.priceMin != null && price > 0 && price < box.priceMin) misses.push("under price floor");
  if (box.priceMax != null && price > box.priceMax) misses.push("over price ceiling");
  if (box.propertyType !== "any") {
    const units = (deal.u2c || 0) + (deal.u1c || 0);
    const [lo, hi] = UNITS[box.propertyType];
    if (units < lo || units > hi) misses.push(`not a ${box.propertyType}`);
  }
  if (box.markets.length) {
    const hay = (deal.market + " " + deal.addr).toLowerCase();
    if (!box.markets.some((mk) => mk.trim() && hay.includes(mk.trim().toLowerCase()))) misses.push("outside target market");
  }
  if (box.floodXOnly && deal.flood !== "X") misses.push(`flood zone ${deal.flood}`);
  if (box.minCap != null && m.capOnCost < box.minCap) misses.push("cap below floor");
  if (box.minCeiling != null && deal.safmr2 > 0 && deal.safmr2 < box.minCeiling) misses.push("rent ceiling too low");
  return misses;
}

export function matchesBuyBox(deal: Deal, m: Metrics, box: BuyBox): boolean {
  return buyBoxMisses(deal, m, box).length === 0;
}

/** True when the box actually constrains anything (else "show all"). */
export function buyBoxActive(box: BuyBox): boolean {
  return (
    box.priceMin != null || box.priceMax != null || box.propertyType !== "any" || box.markets.length > 0 ||
    box.minScore != null || box.floodXOnly || box.minCap != null || box.minCeiling != null || box.excludeGated
  );
}

let counter = 0;
export function newBuyBoxId(): string {
  counter += 1;
  return "bb" + Date.now().toString(36) + counter.toString(36);
}

// --- persistence (cloud `buy_boxes` with graceful local fallback) -----------
const LS = "beacon_buyboxes";
function localAll(): BuyBox[] {
  try {
    const raw = localStorage.getItem(LS);
    if (raw) return JSON.parse(raw) as BuyBox[];
  } catch { /* ignore */ }
  return [];
}
function localSave(all: BuyBox[]) {
  try { localStorage.setItem(LS, JSON.stringify(all)); } catch { /* ignore */ }
}

export async function listBuyBoxes(): Promise<BuyBox[]> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { data, error } = await sb.from("buy_boxes").select("*").order("created_at");
      if (error) throw error;
      if (data) return data.map((r: { id: string; name: string; payload: Omit<BuyBox, "id" | "name"> }) => ({ id: r.id, name: r.name, ...r.payload }));
    } catch { /* fall through to local */ }
  }
  return localAll();
}

export async function saveBuyBox(b: BuyBox): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { id, name, ...payload } = b;
      const { error } = await sb.from("buy_boxes").upsert({ id, name, payload });
      if (error) throw error;
      return;
    } catch { /* fall through */ }
  }
  const all = localAll();
  const i = all.findIndex((x) => x.id === b.id);
  if (i >= 0) all[i] = b; else all.push(b);
  localSave(all);
}

export async function deleteBuyBox(id: string): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { error } = await sb.from("buy_boxes").delete().eq("id", id);
      if (error) throw error;
      return;
    } catch { /* fall through */ }
  }
  localSave(localAll().filter((x) => x.id !== id));
}
